import { parseDayIso } from './day';

/**
 * Geometry for a horizontal schedule board — pure, no React, no domain knowledge.
 *
 * Time runs horizontally: one row per technician, one column per hour. Every conversion between an
 * ISO datetime and an x offset goes through here, so the axis can be re-scaled by touching
 * `HOUR_WIDTH` alone.
 *
 * All arithmetic is in **local** time. `worker-calendar`'s `calendarConstants.ts` mixes a local
 * `getTopPx` with a UTC `getHourFromIso`; that inconsistency is not reproduced here.
 */

/** First hour drawn. Mirrors `CAL_START_HOUR` in the worker calendar. */
export const BOARD_START_HOUR = 6;
/** Last hour drawn. Mirrors `CAL_END_HOUR`. */
export const BOARD_END_HOUR = 22;

export const BOARD_TOTAL_MINUTES = (BOARD_END_HOUR - BOARD_START_HOUR) * 60;

/**
 * Pixel width of one hour column.
 *
 * Wide on purpose: a block carries four lines of text (time, Ort, Gewerk, Auftragsnummer), and the
 * deleted timeline could afford them because it sized blocks as a percentage of a full-width day.
 * On a fixed axis, 120px/h left a 30-minute block at 60px — unreadable. 180 keeps it legible.
 */
export const HOUR_WIDTH = 180;

/** Total width of the time grid, excluding the technician column. */
export const BOARD_WIDTH = (BOARD_END_HOUR - BOARD_START_HOUR) * HOUR_WIDTH;

/** Hour labels for the header — 06:00 … 22:00. */
export const BOARD_HOURS: number[] = Array.from(
  { length: BOARD_END_HOUR - BOARD_START_HOUR + 1 },
  (_, i) => BOARD_START_HOUR + i,
);

/** Width of the sticky technician column. Must match `TechnicianInfo`'s `w-56`. */
export const TECHNICIAN_COL_WIDTH = 224;

/**
 * Height of one lane. A row with two overlapping entries is two lanes tall.
 * 64 is the floor for a work segment carrying four lines of text plus a badge.
 */
export const LANE_HEIGHT = 64;

/** Vertical breathing room above and below the lanes of a row. */
export const ROW_PADDING_Y = 8;

/**
 * Floor for a rendered block, so a 5-minute entry stays clickable-sized. Applied at render only —
 * never feed an inflated width back into `packIntoLanes`, or two short adjacent entries get pushed
 * onto separate lanes.
 */
export const MIN_BLOCK_WIDTH = 8;

const MS_PER_MINUTE = 60_000;

export interface BoardSpan {
  /** Minutes from `BOARD_START_HOUR`, clamped into the drawn window. */
  startMin: number;
  endMin: number;
  /** The entry really begins before the window — draw a flat left edge. */
  clippedStart: boolean;
  /** The entry really ends after the window — draw a flat right edge. */
  clippedEnd: boolean;
}

/**
 * Minutes from the window start, **unclamped** — negative before `BOARD_START_HOUR`, past
 * `BOARD_TOTAL_MINUTES` after `BOARD_END_HOUR`. `NaN` if either argument does not parse.
 *
 * `toBoardSpan` clamps, which is what drawing a block wants but not what a drag preview wants: on a
 * clipped block `span.startMin` is the window edge rather than where the entry really begins, so a
 * ghost built from it would sit offset from the block it came from.
 */
export function rawBoardMinutes(iso: string, dayIso: string): number {
  const day = parseDayIso(dayIso);
  const ms = new Date(iso).getTime();
  if (!day || Number.isNaN(ms)) return Number.NaN;

  const windowStartMs = day.getTime() + BOARD_START_HOUR * 60 * MS_PER_MINUTE;
  return (ms - windowStartMs) / MS_PER_MINUTE;
}

/**
 * Clamps an entry into the drawn window, or returns `null` when it falls entirely outside.
 *
 * This is what makes all-day absences and entries spilling in from the previous day safe to render:
 * the caller never has to special-case them.
 */
export function toBoardSpan(
  startIso: string,
  endIso: string,
  dayIso: string,
): BoardSpan | null {
  const startMin = rawBoardMinutes(startIso, dayIso);
  const endMin = rawBoardMinutes(endIso, dayIso);
  if (Number.isNaN(startMin) || Number.isNaN(endMin)) return null;

  // A zero-length entry is kept; one that merely touches an edge is not.
  if (endMin < startMin) return null;
  if (endMin <= 0 || startMin >= BOARD_TOTAL_MINUTES) return null;

  return {
    startMin: Math.max(startMin, 0),
    endMin: Math.min(endMin, BOARD_TOTAL_MINUTES),
    clippedStart: startMin < 0,
    clippedEnd: endMin > BOARD_TOTAL_MINUTES,
  };
}

/**
 * The technician's shift as board minutes, clamped to the drawn window. `null` when the profile
 * carries no shift, or the two boundaries do not describe a positive span.
 */
export function shiftWindow(
  shiftStart: string | null,
  shiftEnd: string | null,
): { startMin: number; endMin: number } | null {
  if (!shiftStart || !shiftEnd) return null;

  const startHour = shiftHourFromIso(shiftStart);
  const endHour = shiftHourFromIso(shiftEnd);
  if (Number.isNaN(startHour) || Number.isNaN(endHour)) return null;

  const startMin = Math.max(0, (startHour - BOARD_START_HOUR) * 60);
  const endMin = Math.min(BOARD_TOTAL_MINUTES, (endHour - BOARD_START_HOUR) * 60);

  return endMin > startMin ? { startMin, endMin } : null;
}

/**
 * Cuts a span down to the technician's shift, or `null` when nothing of it falls inside.
 *
 * An edge the shift moved is reported as **clipped**, which is what it is: the drawn edge is no
 * longer where the entry starts or ends. That keeps the flat-edge affordance honest and keeps
 * `resolveDragPolicy` treating the block as inert — resizing an edge you cannot see would write a
 * time nobody asked for.
 */
export function clampSpanToShift(
  span: BoardSpan,
  shift: { startMin: number; endMin: number },
): BoardSpan | null {
  const startMin = Math.max(span.startMin, shift.startMin);
  const endMin = Math.min(span.endMin, shift.endMin);
  if (endMin <= startMin) return null;

  return {
    startMin,
    endMin,
    clippedStart: span.clippedStart || startMin > span.startMin,
    clippedEnd: span.clippedEnd || endMin < span.endMin,
  };
}

/**
 * `HH:mm` in local time. Restored from `shared/lib/timeline/dateHelpers.ts`, which went with the
 * deleted timeline; the block labels are the only thing that still needs it.
 */
export function formatTimeHHmm(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Minutes from the window start → a `HH:mm` clock label, for the drag ghost. */
export function formatBoardMinutes(minutes: number): string {
  const total = Math.round(BOARD_START_HOUR * 60 + minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Minutes from the window start → pixel offset. */
export function toXPx(minutes: number): number {
  return (minutes / 60) * HOUR_WIDTH;
}

/** Rendered width of a span, floored at `MIN_BLOCK_WIDTH`. */
export function spanWidthPx(span: BoardSpan): number {
  return Math.max(MIN_BLOCK_WIDTH, toXPx(span.endMin) - toXPx(span.startMin));
}

/**
 * Decimal hour of a shift boundary, read in **UTC** — matching `getHourFromIso` in the worker
 * calendar.
 *
 * This is not the local/UTC inconsistency called out above: `shiftStart` / `shiftEnd` are a
 * wall-clock time-of-day the profile stores inside a datetime, not a real instant, so the stored
 * hour is the intended one. Entry times, which *are* instants, stay local.
 */
export function shiftHourFromIso(iso: string): number {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return Number.NaN;
  return date.getUTCHours() + date.getUTCMinutes() / 60;
}

/**
 * Where "now" sits on the axis, or `null` when the clock is outside the drawn window.
 * `now` is injected so the caller controls the tick and this stays pure.
 */
export function nowOffsetMinutes(now: Date): number | null {
  const minutes = now.getHours() * 60 + now.getMinutes() - BOARD_START_HOUR * 60;
  if (minutes < 0 || minutes > BOARD_TOTAL_MINUTES) return null;
  return minutes;
}
